import React, { useEffect } from 'react'
import { useAppDispatch } from 'app/hooks'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import { createCrossTab } from 'utils/runSearchUtil'
import { toFilter } from 'utils/widgetUtil'
import { Dashboard } from 'schemas/DashboardSchema'
import { RunSearch } from 'schemas/action/RunSearchSchema'
import { getMeasureType } from 'utils/viewOptionUtil'
import { getMetricSeries } from 'utils/measureTypeUtil'
import MetricTimeUnit from 'enums/MetricTimeUnit'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { CrosstabWidgetsType } from '../../types'
import { getWidgetSettingConfig, getSettingTopicInfos } from '../utils/crosstabDataUtils'
import { servicePropertySetter, grandTotalPropertySetter } from './utils'

const createSearches = (
  widgetSetting: CrosstabWidgetsType,
  dashboardData: Dashboard,
  isAudienceQuery: boolean
): RunSearch[] => {
  const { id: dashboardId, name } = dashboardData
  const { precision, viewOption } = widgetSetting
  const topicIds = getSettingTopicInfos(widgetSetting).map((topicInfo) => topicInfo.topicId!)
  const filter = toFilter(widgetSetting)
  const measureTypes = getMeasureType(viewOption!)
  const { displayRowGrandTotal } = getWidgetSettingConfig(widgetSetting)

  const searches = []
  const baseResultSearch = createCrossTab(
    dashboardId!,
    name!,
    precision!,
    topicIds,
    filter,
    measureTypes.flatMap((measure) => getMetricSeries(measure)),
    [MetricTimeUnit.ALL],
    {},
    servicePropertySetter.bind(null, widgetSetting),
    isAudienceQuery
  )
  searches.push(baseResultSearch)

  if (displayRowGrandTotal) {
    const rowGrandTotalSearch = createCrossTab(
      dashboardId!,
      name!,
      precision!,
      topicIds,
      filter,
      measureTypes.flatMap((measure) => getMetricSeries(measure)),
      [MetricTimeUnit.ALL],
      {},
      grandTotalPropertySetter.bind(null, widgetSetting),
      isAudienceQuery
    )
    searches.push(rowGrandTotalSearch)
  }

  return searches
}

interface RunSearchEffectProps {
  widgetId: number
  children: React.ReactElement
  isAudienceQuery?: boolean
}

const RunSearchEffect = ({
  widgetId,
  children,
  isAudienceQuery = false
}: RunSearchEffectProps): React.ReactElement => {
  const widgetSetting = useDashboardWidgetSetting<CrosstabWidgetsType>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const searches = createSearches(widgetSetting, dashboardData!, isAudienceQuery)

    dispatch(
      getWidgetForDashboard({
        searches,
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, dispatch, isAudienceQuery, refreshTimestamp])

  return children
}

export default RunSearchEffect
